import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { roleAPI } from '../services/api';
import './RoleManagement.css';

const RoleManagement = () => {
  const { currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    level: 5,
    permissions: [],
    description: '',
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleAPI.getAll();
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      alert('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await roleAPI.getPermissions();
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRole) {
        await roleAPI.update(editingRole._id, formData);
        alert('Role updated successfully!');
      } else {
        await roleAPI.create(formData);
        alert('Role created successfully!');
      }
      
      setShowModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      alert('Error saving role: ' + (error.message || 'Unknown error'));
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      level: role.level,
      permissions: role.permissions || [],
      description: role.description || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      await roleAPI.delete(id);
      alert('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      alert('Error deleting role: ' + (error.message || 'Unknown error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      level: 5,
      permissions: [],
      description: '',
    });
    setEditingRole(null);
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: (prev.permissions || []).includes(permissionId)
        ? (prev.permissions || []).filter(p => p !== permissionId)
        : [...(prev.permissions || []), permissionId],
    }));
  };

  const handleSelectAllPermissions = () => {
    if (!permissions || permissions.length === 0) return;
    
    if (formData.permissions.length === permissions.length) {
      setFormData(prev => ({ ...prev, permissions: [] }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        permissions: permissions.map(p => p.id) 
      }));
    }
  };

  // Group permissions by module
  const groupedPermissions = (permissions || []).reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = [];
    }
    acc[perm.module].push(perm);
    return acc;
  }, {});

  if (loading) {
    return <div className="loading">Loading roles...</div>;
  }

  return (
    <div className="role-management">
      <div className="role-header">
        <div>
          <h2>Role Management</h2>
          <p>Create and manage roles with custom permissions</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          + Create New Role
        </button>
      </div>

      <div className="roles-grid">
        {(roles || []).map(role => (
          <div key={role._id} className="role-card">
            <div className="role-card-header">
              <h3>{role.name}</h3>
              <span className="role-level">Level {role.level}</span>
            </div>
            
            <p className="role-description">
              {role.description || 'No description provided'}
            </p>

            <div className="role-permissions">
              <strong>Permissions ({(role.permissions || []).length}):</strong>
              <div className="permission-tags">
                {(role.permissions || []).slice(0, 5).map(perm => (
                  <span key={perm} className="permission-tag">
                    {perm}
                  </span>
                ))}
                {(role.permissions || []).length > 5 && (
                  <span className="permission-tag more">
                    +{(role.permissions || []).length - 5} more
                  </span>
                )}
              </div>
            </div>

            <div className="role-actions">
              <button 
                className="btn-edit"
                onClick={() => handleEdit(role)}
              >
                Edit
              </button>
              <button 
                className="btn-delete"
                onClick={() => handleDelete(role._id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Manager, Team Lead"
                  required
                />
              </div>

              <div className="form-group">
                <label>Level *</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
                  required
                />
                <small>Lower number = Higher priority (1 is highest)</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this role..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <div className="permissions-header">
                  <label>Permissions *</label>
                  <button
                    type="button"
                    className="btn-select-all"
                    onClick={handleSelectAllPermissions}
                  >
                    {(formData.permissions || []).length === (permissions || []).length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className="permissions-grid">
                  {Object.entries(groupedPermissions).map(([module, perms]) => (
                    <div key={module} className="permission-module">
                      <h4>{module}</h4>
                      {perms.map(perm => (
                        <label key={perm.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={(formData.permissions || []).includes(perm.id)}
                            onChange={() => handlePermissionToggle(perm.id)}
                          />
                          <span>{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
